import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

const API_BASE_URL = `${environment.backendBaseUrl}/api/`;

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  // Auth
  public auth = {
    login: API_BASE_URL + 'auth/login',
    logout: API_BASE_URL + 'auth/logout',
    refreshToken: API_BASE_URL + 'auth/refresh-token',
    forgetPassword: API_BASE_URL + 'auth/forget-password',
    resetPassword: (token: string) =>
      API_BASE_URL + 'auth/reset-password/' + token,
    changePassword: API_BASE_URL + 'auth/change-password',
    refresh: API_BASE_URL + 'auth/refresh',
    runAs: API_BASE_URL + 'auth/run-as',
  };

  // Home
  public home = {
    tasksStatistics: API_BASE_URL + 'home/tasks-statistics',
    notificationsStatistics: API_BASE_URL + 'home/notifications-statistics',
    notifications: API_BASE_URL + 'home/notifications',
    workspaceProjects: API_BASE_URL + 'home/workspace-projects',
    clientProjects: API_BASE_URL + 'home/projects',
    stories: API_BASE_URL + 'home/stories',
    workspaceProjectTasks: API_BASE_URL + 'home/workspace-project-tasks',
    meetingTasks: API_BASE_URL + 'home/meeting-tasks',
    generalTasks: API_BASE_URL + 'home/general-tasks',
    contracts: API_BASE_URL + 'home/contracts',
  };

  // Dashboard
  public dashboard = {
    statistics: API_BASE_URL + 'dashboard/statistics',
    weeklyTasks: API_BASE_URL + 'dashboard/weekly-tasks',
  };

  // Users
  public users = {
    list: API_BASE_URL + 'users/list',
    details: (id: number) => API_BASE_URL + 'users/details/' + id,
    profile: (id: number) => API_BASE_URL + 'users/profile/' + id,
    projects: (id: number) => API_BASE_URL + 'users/projects/' + id,
    files: (id: number) => API_BASE_URL + 'users/files/' + id,
    features: API_BASE_URL + 'users/features',
    allFeatures: API_BASE_URL + 'users/all-features',
    closeFeatures: API_BASE_URL + 'users/close-features',
    permissions: (id: number) => API_BASE_URL + 'users/permissions/' + id,
    customizePermissions: (id: number) =>
      API_BASE_URL + 'users/customize-permissions/' + id,
    resetCustomizedPermissions: (id: number) =>
      API_BASE_URL + 'users/reset-customized-permissions/' + id,
    notifications: API_BASE_URL + 'users/notifications',
    readNotifications: (id: number) =>
      API_BASE_URL + 'users/notifications/' + id + '/read',
    readAllNotifications: API_BASE_URL + 'users/notifications/read-all',
    hideNotification: (id: number) =>
      API_BASE_URL + 'users/notifications/' + id + '/hide',
    hideAllNotifications: API_BASE_URL + 'users/notifications/hide-all',
    unReadNotifications: API_BASE_URL + 'users/unread-notifications',

    add: API_BASE_URL + 'users/add',
    edit: (id: number) => API_BASE_URL + 'users/edit/' + id,
    delete: (id: number) => API_BASE_URL + 'users/delete/' + id,
    active: (id: number) => API_BASE_URL + 'users/active/' + id,
    invite: (id: number) => API_BASE_URL + 'users/invite/' + id,
    sendEmail: (id: number) => API_BASE_URL + 'users/send-email/' + id,
    editMyProfile: API_BASE_URL + 'users/edit-my-profile',
    resetPassword: (id: number) => API_BASE_URL + 'users/reset-password/' + id,
    takeScreenshot: (id: number) =>
      API_BASE_URL + 'users/take-screenshot/' + id,
    getScreenshots: (id: number) => API_BASE_URL + 'users/screenshots/' + id,
    sendNotification: API_BASE_URL + 'users/send-notification',
    sendFeedback: API_BASE_URL + 'users/send-feedback',
  };

  // Clients
  public clients = {
    list: API_BASE_URL + 'clients/list',
    details: (id: number) => API_BASE_URL + 'clients/details/' + id,
    profile: (id: number) => API_BASE_URL + 'clients/profile/' + id,
    projects: (id: number) => API_BASE_URL + 'clients/projects/' + id,
    files: (id: number) => API_BASE_URL + 'clients/files/' + id,
    contracts: API_BASE_URL + 'clients/contracts',
    invoices: API_BASE_URL + 'clients/invoices',
    features: API_BASE_URL + 'clients/features',
    allFeatures: API_BASE_URL + 'clients/all-features',
    closeFeatures: API_BASE_URL + 'clients/close-features',
    permissions: (id: number) => API_BASE_URL + 'clients/permissions/' + id,
    customizePermissions: (id: number) =>
      API_BASE_URL + 'clients/customize-permissions/' + id,
    resetCustomizedPermissions: (id: number) =>
      API_BASE_URL + 'clients/reset-customized-permissions/' + id,
    notifications: API_BASE_URL + 'clients/notifications',
    readNotifications: (id: number) =>
      API_BASE_URL + 'clients/notifications/' + id + '/read',
    readAllNotifications: API_BASE_URL + 'clients/notifications/read-all',
    hideNotification: (id: number) =>
      API_BASE_URL + 'clients/notifications/' + id + '/hide',
    hideAllNotifications: API_BASE_URL + 'clients/notifications/hide-all',
    unReadNotifications: API_BASE_URL + 'clients/unread-notifications',

    add: API_BASE_URL + 'clients/add',
    register: API_BASE_URL + 'clients/register',
    edit: (id: number) => API_BASE_URL + 'clients/edit/' + id,
    delete: (id: number) => API_BASE_URL + 'clients/delete/' + id,
    active: (id: number) => API_BASE_URL + 'clients/active/' + id,
    approve: (id: number) => API_BASE_URL + 'clients/approve/' + id,
    invite: (id: number) => API_BASE_URL + 'clients/invite/' + id,
    sendEmail: (id: number) => API_BASE_URL + 'clients/send-email/' + id,
    editMyProfile: API_BASE_URL + 'clients/edit-my-profile',
    resetPassword: (id: number) =>
      API_BASE_URL + 'clients/reset-password/' + id,
    sendNotification: API_BASE_URL + 'clients/send-notification',
    sendFeedback: API_BASE_URL + 'clients/send-feedback',
  };

  // Teams
  public teams = {
    list: API_BASE_URL + 'teams/list',

    add: API_BASE_URL + 'teams/add',
    edit: (id: number) => API_BASE_URL + 'teams/edit/' + id,
    delete: (id: number) => API_BASE_URL + 'teams/delete/' + id,
  };

  // Workspaces
  public workspaces = {
    list: API_BASE_URL + 'workspaces/list',
    details: (id: number) => API_BASE_URL + 'workspaces/details/' + id,
    lists: (id: number) => API_BASE_URL + 'workspaces/lists/' + id,
    users: (id: number) => API_BASE_URL + 'workspaces/users/' + id,
    defaultUsers: (id: number) =>
      API_BASE_URL + 'workspaces/default-users/' + id,
    tags: (id: number) => API_BASE_URL + 'workspaces/tags/' + id,

    add: API_BASE_URL + 'workspaces/add',
    edit: (id: number) => API_BASE_URL + 'workspaces/edit/' + id,
    manageTags: (id: number) => API_BASE_URL + 'workspaces/manage-tags/' + id,
    delete: (id: number) => API_BASE_URL + 'workspaces/delete/' + id,
    export: (id: number) => API_BASE_URL + 'workspaces/export/' + id,
  };

  // Workspace Projects
  public workspaceProjects = {
    list: API_BASE_URL + 'workspaceProjects/list',
    board: (id: number) => API_BASE_URL + 'workspaceProjects/board/' + id,
    moreProjects: (id: number, listId: number) =>
      API_BASE_URL +
      'workspaceProjects/more-board-projects/' +
      id +
      '/list/' +
      listId,
    overview: (id: number) => API_BASE_URL + 'workspaceProjects/overview/' + id,
    details: (id: number) => API_BASE_URL + 'workspaceProjects/details/' + id,
    lists: (id: number) => API_BASE_URL + 'workspaceProjects/lists/' + id,
    users: (id: number) => API_BASE_URL + 'workspaceProjects/users/' + id,
    allTeamUsers: (id: number) =>
      API_BASE_URL + 'workspaceProjects/all-team-users/' + id,

    add: API_BASE_URL + 'workspaceProjects/add',
    edit: (id: number) => API_BASE_URL + 'workspaceProjects/edit/' + id,
    delete: (id: number) => API_BASE_URL + 'workspaceProjects/delete/' + id,
    move: API_BASE_URL + 'workspaceProjects/move',
    clone: API_BASE_URL + 'workspaceProjects/clone',
    assign: API_BASE_URL + 'workspaceProjects/assign',

    updatePosition: API_BASE_URL + 'workspaceProjects/update-position',
    changeList: API_BASE_URL + 'workspaceProjects/change-list',

    addToMonthlyInvoice:
      API_BASE_URL + 'workspaceProjects/add-to-monthly-invoice',

    start: (id: number) => API_BASE_URL + 'workspaceProjects/start/' + id,
    pause: (id: number) => API_BASE_URL + 'workspaceProjects/pause/' + id,
    resume: (id: number) => API_BASE_URL + 'workspaceProjects/resume/' + id,
    complete: (id: number) => API_BASE_URL + 'workspaceProjects/complete/' + id,
    close: (id: number) => API_BASE_URL + 'workspaceProjects/close/' + id,
  };

  // Projects
  public projects = {
    list: API_BASE_URL + 'projects/list',
    overview: (id: number) => API_BASE_URL + 'projects/overview/' + id,
    details: (id: number) => API_BASE_URL + 'projects/details/' + id,
    costs: (id: number) => API_BASE_URL + 'projects/costs/' + id,
    users: (id: number) => API_BASE_URL + 'projects/users/' + id,

    delete: (id: number) => API_BASE_URL + 'projects/delete/' + id,
  };

  // Stories
  public stories = {
    list: API_BASE_URL + 'stories/list',
    board: (id: number) => API_BASE_URL + 'stories/board/' + id,
    moreStories: (id: number, listId: number) =>
      API_BASE_URL + 'stories/more-board-stories/' + id + '/list/' + listId,
    overview: (id: number) => API_BASE_URL + 'stories/overview/' + id,
    details: (id: number) => API_BASE_URL + 'stories/details/' + id,
    users: (id: number) => API_BASE_URL + 'stories/users/' + id,

    add: API_BASE_URL + 'stories/add',
    edit: (id: number) => API_BASE_URL + 'stories/edit/' + id,
    submitEstimate: (id: number) =>
      API_BASE_URL + 'stories/submit-estimate/' + id,
    delete: (id: number) => API_BASE_URL + 'stories/delete/' + id,
    approve: (id: number) => API_BASE_URL + 'stories/approve/' + id,
    reject: (id: number) => API_BASE_URL + 'stories/reject/' + id,

    updatePosition: API_BASE_URL + 'stories/update-position',
    changeList: API_BASE_URL + 'stories/change-list',
    changeInvoiceMonth: API_BASE_URL + 'stories/change-invoice-month',
  };

  // Workspace Project Tasks
  public workspaceProjectTasks = {
    list: API_BASE_URL + 'tasks/workspace-project-list',
    overview: (id: number) =>
      API_BASE_URL + 'tasks/workspace-project-task-overview/' + id,
    details: (id: number) =>
      API_BASE_URL + 'tasks/workspace-project-task-details/' + id,
    users: (id: number) =>
      API_BASE_URL + 'tasks/workspace-project-task-users/' + id,
    board: (id: number) => API_BASE_URL + 'tasks/board/' + id,
    moreTasks: (id: number, listId: number) =>
      API_BASE_URL + 'tasks/more-board-tasks/' + id + '/list/' + listId,

    add: API_BASE_URL + 'tasks/add-workspace-project-task',
    edit: (id: number) =>
      API_BASE_URL + 'tasks/edit-workspace-project-task/' + id,
    delete: (id: number) =>
      API_BASE_URL + 'tasks/delete-workspace-project-task/' + id,
    sendTaskReminder: (id: number) =>
      API_BASE_URL + 'tasks/send-workspace-project-task-reminder/' + id,

    assignTo: (id: number, userId: number) =>
      API_BASE_URL +
      'tasks/assign-workspace-project-task/' +
      id +
      '/to/' +
      userId,
    start: (id: number) =>
      API_BASE_URL + 'tasks/start-workspace-project-task/' + id,
    pause: (id: number) =>
      API_BASE_URL + 'tasks/pause-workspace-project-task/' + id,
    resume: (id: number) =>
      API_BASE_URL + 'tasks/resume-workspace-project-task/' + id,
    complete: (id: number) =>
      API_BASE_URL + 'tasks/complete-workspace-project-task/' + id,
    approve: (id: number) =>
      API_BASE_URL + 'tasks/approve-workspace-project-task/' + id,
    reject: (id: number) =>
      API_BASE_URL + 'tasks/reject-workspace-project-task/' + id,
    statistics: (id: number) =>
      API_BASE_URL + 'tasks/workspace-project-task-statistics/' + id,
    updateStatistics: (id: number) =>
      API_BASE_URL + 'tasks/update-workspace-project-task-statistics/' + id,
  };

  // Meeting Tasks
  public meetingTasks = {
    list: API_BASE_URL + 'tasks/meeting-list',
    overview: (id: number) =>
      API_BASE_URL + 'tasks/meeting-task-overview/' + id,
    details: (id: number) => API_BASE_URL + 'tasks/meeting-task-details/' + id,
    users: (id: number) => API_BASE_URL + 'tasks/meeting-task-users/' + id,

    add: API_BASE_URL + 'tasks/add-meeting-task',
    edit: (id: number) => API_BASE_URL + 'tasks/edit-meeting-task/' + id,
    delete: (id: number) => API_BASE_URL + 'tasks/delete-meeting-task/' + id,
    sendTaskReminder: (id: number) =>
      API_BASE_URL + 'tasks/send-meeting-task-reminder/' + id,

    assignTo: (id: number, userId: number) =>
      API_BASE_URL + 'tasks/assign-meeting-task/' + id + '/to/' + userId,
    start: (id: number) => API_BASE_URL + 'tasks/start-meeting-task/' + id,
    pause: (id: number) => API_BASE_URL + 'tasks/pause-meeting-task/' + id,
    resume: (id: number) => API_BASE_URL + 'tasks/resume-meeting-task/' + id,
    complete: (id: number) =>
      API_BASE_URL + 'tasks/complete-meeting-task/' + id,
    approve: (id: number) => API_BASE_URL + 'tasks/approve-meeting-task/' + id,
    reject: (id: number) => API_BASE_URL + 'tasks/reject-meeting-task/' + id,
    statistics: (id: number) =>
      API_BASE_URL + 'tasks/meeting-task-statistics/' + id,
    updateStatistics: (id: number) =>
      API_BASE_URL + 'tasks/update-meeting-task-statistics/' + id,
  };

  // General Tasks
  public generalTasks = {
    list: API_BASE_URL + 'tasks/general-list',
    overview: (id: number) =>
      API_BASE_URL + 'tasks/general-task-overview/' + id,
    details: (id: number) => API_BASE_URL + 'tasks/general-task-details/' + id,
    users: (id: number) => API_BASE_URL + 'tasks/general-task-users/' + id,

    add: API_BASE_URL + 'tasks/add-general-task',
    edit: (id: number) => API_BASE_URL + 'tasks/edit-general-task/' + id,
    delete: (id: number) => API_BASE_URL + 'tasks/delete-general-task/' + id,
    sendTaskReminder: (id: number) =>
      API_BASE_URL + 'tasks/send-general-task-reminder/' + id,

    assignTo: (id: number, userId: number) =>
      API_BASE_URL + 'tasks/assign-general-task/' + id + '/to/' + userId,
    start: (id: number) => API_BASE_URL + 'tasks/start-general-task/' + id,
    pause: (id: number) => API_BASE_URL + 'tasks/pause-general-task/' + id,
    resume: (id: number) => API_BASE_URL + 'tasks/resume-general-task/' + id,
    complete: (id: number) =>
      API_BASE_URL + 'tasks/complete-general-task/' + id,
    approve: (id: number) => API_BASE_URL + 'tasks/approve-general-task/' + id,
    reject: (id: number) => API_BASE_URL + 'tasks/reject-general-task/' + id,
    statistics: (id: number) =>
      API_BASE_URL + 'tasks/general-task-statistics/' + id,
    updateStatistics: (id: number) =>
      API_BASE_URL + 'tasks/update-general-task-statistics/' + id,
  };

  // Lists
  public lists = {
    // Workspace Lists
    getWorkspaceList: (workspace_id: number, list_id: number) =>
      API_BASE_URL +
      'lists/get-workspace-list/' +
      workspace_id +
      '/list/' +
      list_id,
    addWorkspaceList: API_BASE_URL + 'lists/add-workspace-list',
    editWorkspaceList: API_BASE_URL + 'lists/edit-workspace-list',
    assignResourceWorkspaceList:
      API_BASE_URL + 'lists/assign-resource-workspace-list',
    deleteWorkspaceList: (id: number) =>
      API_BASE_URL + 'lists/delete-workspace-list/' + id,
    updateOrderWorkspaceLists:
      API_BASE_URL + 'lists/update-order-workspace-lists',

    // Workspace Project Lists
    getWorkspaceProjectList: (workspace_project_id: number, list_id: number) =>
      API_BASE_URL +
      'lists/get-workspace-project-list/' +
      workspace_project_id +
      '/list/' +
      list_id,
    addWorkspaceProjectList: API_BASE_URL + 'lists/add-workspace-project-list',
    editWorkspaceProjectList:
      API_BASE_URL + 'lists/edit-workspace-project-list',
    deleteWorkspaceProjectList: (id: number) =>
      API_BASE_URL + 'lists/delete-workspace-project-list/' + id,
    updateOrderWorkspaceProjectLists:
      API_BASE_URL + 'lists/update-order-workspace-project-lists',

    // Project Lists
    getProjectList: (project_id: number, list_id: number) =>
      API_BASE_URL +
      'lists/get-project-list/' +
      project_id +
      '/list/' +
      list_id,
    addProjectList: API_BASE_URL + 'lists/add-project-list',
    editProjectList: API_BASE_URL + 'lists/edit-project-list',
    deleteProjectList: (id: number) =>
      API_BASE_URL + 'lists/delete-project-list/' + id,
    updateOrderProjectLists: API_BASE_URL + 'lists/update-order-project-lists',
  };

  // Meetings
  public meetings = {
    list: API_BASE_URL + 'meetings/list',
    overview: (id: number) => API_BASE_URL + 'meetings/overview/' + id,
    details: (id: number) => API_BASE_URL + 'meetings/details/' + id,

    add: API_BASE_URL + 'meetings/add',
    request: API_BASE_URL + 'meetings/request',
    edit: (id: number) => API_BASE_URL + 'meetings/edit/' + id,
    delete: (id: number) => API_BASE_URL + 'meetings/delete/' + id,
    approveRequest: (id: number) =>
      API_BASE_URL + 'meetings/approve-request/' + id,
    changeInvoiceMonth: API_BASE_URL + 'meetings/change-invoice-month',

    active: (id: number) => API_BASE_URL + 'meetings/active/' + id,
    start: (id: number) => API_BASE_URL + 'meetings/start/' + id,
    finish: (id: number) => API_BASE_URL + 'meetings/finish/' + id,
  };

  // ToDo Tasks
  public todo = {
    list: API_BASE_URL + 'todo/list',

    add: API_BASE_URL + 'todo/add',
    edit: (id: number) => API_BASE_URL + 'todo/edit/' + id,
    delete: (id: number) => API_BASE_URL + 'todo/delete/' + id,
    complete: (id: number) => API_BASE_URL + 'todo/complete/' + id,
    share: (id: number) => API_BASE_URL + 'todo/share/' + id,
  };

  // Roles
  public roles = {
    list: API_BASE_URL + 'roles/list',
    details: (id: number) => API_BASE_URL + 'roles/details/' + id,

    add: API_BASE_URL + 'roles/add',
    edit: (id: number) => API_BASE_URL + 'roles/edit/' + id,
    delete: (id: number) => API_BASE_URL + 'roles/delete/' + id,
  };

  // Branches
  public branches = {
    list: API_BASE_URL + 'branches/list',

    add: API_BASE_URL + 'branches/add',
    edit: (id: number) => API_BASE_URL + 'branches/edit/' + id,
    delete: (id: number) => API_BASE_URL + 'branches/delete/' + id,
  };

  // Currencies
  public currencies = {
    list: API_BASE_URL + 'currencies/list',

    add: API_BASE_URL + 'currencies/add',
    edit: (id: number) => API_BASE_URL + 'currencies/edit/' + id,
    delete: (id: number) => API_BASE_URL + 'currencies/delete/' + id,
  };

  // Job Descriptions
  public jobDescriptions = {
    list: API_BASE_URL + 'jobDescriptions/list',

    add: API_BASE_URL + 'jobDescriptions/add',
    edit: (id: number) => API_BASE_URL + 'jobDescriptions/edit/' + id,
    delete: (id: number) => API_BASE_URL + 'jobDescriptions/delete/' + id,
  };

  // Contract Types
  public contractTypes = {
    list: API_BASE_URL + 'contractTypes/list',

    add: API_BASE_URL + 'contractTypes/add',
    edit: (id: number) => API_BASE_URL + 'contractTypes/edit/' + id,
    delete: (id: number) => API_BASE_URL + 'contractTypes/delete/' + id,
  };

  // Departments
  public departments = {
    list: API_BASE_URL + 'departments/list',

    add: API_BASE_URL + 'departments/add',
    edit: (id: number) => API_BASE_URL + 'departments/edit/' + id,
    delete: (id: number) => API_BASE_URL + 'departments/delete/' + id,
    updateOrderList: API_BASE_URL + 'departments/update-order',
  };

  // Companies
  public companies = {
    list: API_BASE_URL + 'companies/list',
    details: (id: number) => API_BASE_URL + 'companies/details/' + id,

    add: API_BASE_URL + 'companies/add',
    edit: (id: number) => API_BASE_URL + 'companies/edit/' + id,
    delete: (id: number) => API_BASE_URL + 'companies/delete/' + id,
  };

  // Banks
  public banks = {
    list: API_BASE_URL + 'banks/list',
    details: (id: number) => API_BASE_URL + 'banks/details/' + id,

    add: API_BASE_URL + 'banks/add',
    edit: (id: number) => API_BASE_URL + 'banks/edit/' + id,
    delete: (id: number) => API_BASE_URL + 'banks/delete/' + id,
  };

  // Settings
  public settings = {
    list: API_BASE_URL + 'settings/list',
    pages: API_BASE_URL + 'settings/pages',
    edit: API_BASE_URL + 'settings/edit',
  };

  // Common
  public common = {
    users: API_BASE_URL + 'common/users',
    myTeamUsers: API_BASE_URL + 'common/my-team-users',
    usersForMeeting: API_BASE_URL + 'common/users-for-meeting',
    clients: API_BASE_URL + 'common/clients',
    workspaces: API_BASE_URL + 'common/workspaces',
    workspaceProjects: API_BASE_URL + 'common/workspace-projects',
    projects: API_BASE_URL + 'common/projects',
    meetings: API_BASE_URL + 'common/meetings',
    resources: (id: number) => API_BASE_URL + 'common/resources/' + id,
    permissions: API_BASE_URL + 'common/permissions',
    lookups: API_BASE_URL + 'common/lookups',
  };

  // Attachments
  public attachments = {
    list: {
      workspaceProject: (id: number) =>
        API_BASE_URL + `attachments/workspace-project-attachments-list/${id}`,
      project: (id: number) =>
        API_BASE_URL + `attachments/project-attachments-list/${id}`,
      story: (id: number) =>
        API_BASE_URL + `attachments/story-attachments-list/${id}`,
      workspaceProjectTask: (id: number) =>
        API_BASE_URL +
        `attachments/workspace-project-task-attachments-list/${id}`,
      meetingTask: (id: number) =>
        API_BASE_URL + `attachments/meeting-task-attachments-list/${id}`,
      generalTask: (id: number) =>
        API_BASE_URL + `attachments/general-task-attachments-list/${id}`,
      meeting: (id: number) =>
        API_BASE_URL + `attachments/meeting-attachments-list/${id}`,
    },

    add: {
      workspaceProject: (id: number) =>
        API_BASE_URL + `attachments/add-workspace-project-attachment/${id}`,
      project: (id: number) =>
        API_BASE_URL + `attachments/add-project-attachment/${id}`,
      story: (id: number) =>
        API_BASE_URL + `attachments/add-story-attachment/${id}`,
      workspaceProjectTask: (id: number) =>
        API_BASE_URL +
        `attachments/add-workspace-project-task-attachment/${id}`,
      meetingTask: (id: number) =>
        API_BASE_URL + `attachments/add-meeting-task-attachment/${id}`,
      generalTask: (id: number) =>
        API_BASE_URL + `attachments/add-general-task-attachment/${id}`,
      meeting: (id: number) =>
        API_BASE_URL + `attachments/add-meeting-attachment/${id}`,
    },

    download: {
      workspaceProjectAll: (workspace_project_id: number) =>
        API_BASE_URL +
        `attachments/download-all-workspace-project-attachments/${workspace_project_id}`,
      projectAll: (project_id: number) =>
        API_BASE_URL +
        `attachments/download-all-project-attachments/${project_id}`,
      workspaceProject: (id: number) =>
        API_BASE_URL +
        `attachments/download-workspace-project-attachment/${id}`,
      project: (id: number) =>
        API_BASE_URL + `attachments/download-project-attachment/${id}`,
      story: (id: number) =>
        API_BASE_URL + `attachments/download-story-attachment/${id}`,
      task: (id: number) =>
        API_BASE_URL + `attachments/download-task-attachment/${id}`,
      meeting: (id: number) =>
        API_BASE_URL + `attachments/download-meeting-attachment/${id}`,
    },

    delete: {
      workspaceProject: (id: number) =>
        API_BASE_URL + `attachments/delete-workspace-project-attachment/${id}`,
      project: (id: number) =>
        API_BASE_URL + `attachments/delete-project-attachment/${id}`,
      story: (id: number) =>
        API_BASE_URL + `attachments/delete-story-attachment/${id}`,
      task: (id: number) =>
        API_BASE_URL + `attachments/delete-task-attachment/${id}`,
      meeting: (id: number) =>
        API_BASE_URL + `attachments/delete-meeting-attachment/${id}`,
    },
  };

  // Activities
  public activities = {
    list: {
      workspaceProject: (id: number) =>
        API_BASE_URL + `activities/workspace-project-activities-list/${id}`,
      project: (id: number) =>
        API_BASE_URL + `activities/project-activities-list/${id}`,
      story: (id: number) =>
        API_BASE_URL + `activities/story-activities-list/${id}`,
      workspaceProjectTask: (id: number) =>
        API_BASE_URL +
        `activities/workspace-project-task-activities-list/${id}`,
      meetingTask: (id: number) =>
        API_BASE_URL + `activities/meeting-task-activities-list/${id}`,
      generalTask: (id: number) =>
        API_BASE_URL + `activities/general-task-activities-list/${id}`,
      meeting: (id: number) =>
        API_BASE_URL + `activities/meeting-activities-list/${id}`,
    },

    add: {
      workspaceProject: (id: number) =>
        API_BASE_URL + `activities/add-workspace-project-activity/${id}`,
      project: (id: number) =>
        API_BASE_URL + `activities/add-project-activity/${id}`,
      story: (id: number) =>
        API_BASE_URL + `activities/add-story-activity/${id}`,
      workspaceProjectTask: (id: number) =>
        API_BASE_URL + `activities/add-workspace-project-task-activity/${id}`,
      meetingTask: (id: number) =>
        API_BASE_URL + `activities/add-meeting-task-activity/${id}`,
      generalTask: (id: number) =>
        API_BASE_URL + `activities/add-general-task-activity/${id}`,
      meeting: (id: number) =>
        API_BASE_URL + `activities/add-meeting-activity/${id}`,
    },

    edit: {
      workspaceProject: (id: number) =>
        API_BASE_URL + `activities/edit-workspace-project-activity/${id}`,
      project: (id: number) =>
        API_BASE_URL + `activities/edit-project-activity/${id}`,
      story: (id: number) =>
        API_BASE_URL + `activities/edit-story-activity/${id}`,
      workspaceProjectTask: (id: number) =>
        API_BASE_URL + `activities/edit-workspace-project-task-activity/${id}`,
      meetingTask: (id: number) =>
        API_BASE_URL + `activities/edit-meeting-task-activity/${id}`,
      generalTask: (id: number) =>
        API_BASE_URL + `activities/edit-general-task-activity/${id}`,
      meeting: (id: number) =>
        API_BASE_URL + `activities/edit-meeting-activity/${id}`,
    },

    delete: {
      workspaceProject: (id: number) =>
        API_BASE_URL + `activities/delete-workspace-project-activity/${id}`,
      project: (id: number) =>
        API_BASE_URL + `activities/delete-project-activity/${id}`,
      story: (id: number) =>
        API_BASE_URL + `activities/delete-story-activity/${id}`,
      task: (id: number) =>
        API_BASE_URL + `activities/delete-task-activity/${id}`,
      meeting: (id: number) =>
        API_BASE_URL + `activities/delete-meeting-activity/${id}`,
    },
  };

  // Reports
  public reports = {
    myReport: API_BASE_URL + 'reports/my-report',
    users: API_BASE_URL + 'reports/users-report',
    user: (id: number) => API_BASE_URL + 'reports/user/' + id,
    client: (id: number) => API_BASE_URL + 'reports/client/' + id,
    project: (id: number) => API_BASE_URL + 'reports/project/' + id,
    workspace: (id: number) => API_BASE_URL + 'reports/workspace/' + id,
    downloadMyReportPdf: API_BASE_URL + 'reports/download-my-report',
    downloadUserPdf: (id: number) =>
      API_BASE_URL + 'reports/download-user-report/' + id,
    downloadClientPdf: (id: number) =>
      API_BASE_URL + 'reports/download-client-report/' + id,
  };

  // Quotes
  public quotes = {
    list: API_BASE_URL + 'quotes/list',
    details: (id: number) => API_BASE_URL + 'quotes/details/' + id,
    costs: (id: number) => API_BASE_URL + 'quotes/costs/' + id,

    add: API_BASE_URL + 'quotes/add',
    edit: (id: number) => API_BASE_URL + 'quotes/edit/' + id,
    updateSummary: (id: number) => API_BASE_URL + 'quotes/update-summary/' + id,
    updateStatus: (id: number) => API_BASE_URL + 'quotes/update-status/' + id,
  };

  // Monthly Projects
  public monthlyProjects = {
    list: API_BASE_URL + 'monthlyProjects/list',

    add: API_BASE_URL + 'monthlyProjects/add',
    delete: (id: number) => API_BASE_URL + 'monthlyProjects/delete/' + id,
  };

  // Invoices
  public invoices = {
    list: API_BASE_URL + 'invoices/list',

    generateProjectInvoice: API_BASE_URL + 'invoices/generate-project-invoice',
    generateQuoteInvoice: API_BASE_URL + 'invoices/generate-quote-invoice',
    generateMonthlyInvoice: API_BASE_URL + 'invoices/generate-monthly-invoice',

    download: (id: number) => API_BASE_URL + `invoices/download/${id}`,
    delete: (id: number) => API_BASE_URL + 'invoices/delete/' + id,
    markAsPaid: (id: number) => API_BASE_URL + 'invoices/mark-as-paid/' + id,
  };

  // Contracts
  public contracts = {
    list: API_BASE_URL + 'contracts/list',

    uploadProjectContract: API_BASE_URL + 'contracts/upload-project-contract',
    generateProjectContract:
      API_BASE_URL + 'contracts/generate-project-contract',
    approve: (id: number) => API_BASE_URL + 'contracts/approve/' + id,
    sendToClient: (id: number) =>
      API_BASE_URL + 'contracts/send-to-client/' + id,
    reject: (id: number) => API_BASE_URL + 'contracts/reject/' + id,
    delete: (id: number) => API_BASE_URL + 'contracts/delete/' + id,

    download: (id: number) => API_BASE_URL + `contracts/download/${id}`,
  };

  // Logs
  public logs = {
    list: API_BASE_URL + 'logs/list',
    export: API_BASE_URL + 'logs/export',
  };

  // Federation
  public federation = {
    search: API_BASE_URL + 'federation/search',
    newRecordSearch: API_BASE_URL + 'federation/new-record-search',
    getRecentSearches: API_BASE_URL + 'federation/get-recent-searches',
  };

  // Features
  public features = {
    list: API_BASE_URL + 'features/list',
    permissions: (id: number) => API_BASE_URL + 'features/permissions/' + id,

    add: API_BASE_URL + 'features/add',
    edit: (id: number) => API_BASE_URL + 'features/edit/' + id,
    delete: (id: number) => API_BASE_URL + 'features/delete/' + id,
    active: (id: number) => API_BASE_URL + 'features/active/' + id,
  };

  // Agenda Items
  public agendaItems = {
    add: () => API_BASE_URL + 'agendaItems/add',
    edit: (id: number) => API_BASE_URL + 'agendaItems/edit/' + id,
    delete: (id: number) => API_BASE_URL + 'agendaItems/delete/' + id,
    complete: (id: number) => API_BASE_URL + 'agendaItems/complete/' + id,
  };
}
